import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getPartnerSuggestions,
  clearSuggestions,
} from "../../store/slices/partnersSlice";
import {
  getAddressSuggestions,
  clearSuggestions as clearAddressSuggestions,
} from "../../store/slices/addressesSlice";
import type { PartnerSuggestion, EntitySnapshot } from "../../types";
import { Building2, MapPin, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/*
 * DEAD PROPS — kept commented for reference. Drove the legacy modal-based
 * add/edit flow that has been replaced by inline partner fields. If a
 * Save-as-Partner modal is reintroduced, the snapshot/edit/add affordances
 * below can be re-enabled at the JSX site (see commented block in the render).
 *
 *   snapshot?: EntitySnapshot;           // selected partner pinned by the picker
 *   onAddClick: () => void;              // opens the Add modal
 *   onEditClick: () => void;             // opens the Edit modal pre-filled
 */

interface PartnerAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Fires when the user picks an existing partner / address from the dropdown.
   * Parent should populate the rest of the section (regCode, phone, email,
   * address.*) from the snapshot via setValue().
   */
  onSnapshotSelect: (snapshot: EntitySnapshot) => void;
  /**
   * Fires when the user clicks the X (clear) button. Parent should wipe all
   * inline fields in the section. Optional — if absent, the X just clears
   * the input value via `onChange("")`.
   */
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  /** true = query the addresses slice (pickup/delivery), false = query partners (carrier/client) */
  addresses?: boolean;
  type?: "pickup" | "delivery" | "carrier" | "client";
  size?: "sm" | "md";
}

const PartnerAutocompleteComponent: React.FC<PartnerAutocompleteProps> = ({
  value,
  onChange,
  onSnapshotSelect,
  onClear,
  placeholder = "Enter company name",
  disabled = false,
  error = false,
  className = "",
  addresses = false,
  type,
  size = "md",
}) => {
  const { t } = useTranslation(["shipments", "common"]);

  const dispatch = useAppDispatch();
  const partnerState = useAppSelector((state) => state.partner);
  const addressState = useAppSelector((state) => state.addresses);

  const { suggestions } = addresses ? addressState : partnerState;

  const [localLoading, setLocalLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  /** Debounced search */
  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        if (term.trim().length >= 2) {
          setLocalLoading(true);
          if (addresses) {
            dispatch(
              getAddressSuggestions({ query: term.trim(), limit: 10 }),
            ).finally(() => setLocalLoading(false));
          } else {
            dispatch(
              getPartnerSuggestions({
                query: term.trim(),
                limit: 10,
                // Carrier picker only suggests forwarders; client/other
                // contexts suggest all partners.
                ...(type === "carrier" ? { forwarder: "true" } : {}),
              }),
            ).finally(() => setLocalLoading(false));
          }
          setIsOpen(true);
        } else {
          dispatch(clearSuggestions());
          dispatch(clearAddressSuggestions());
          setIsOpen(false);
        }
      }, 600);
    },
    [dispatch, addresses, type],
  );

  /** Handle input change */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      onChange(newValue);
      debouncedSearch(newValue);
    },
    [debouncedSearch, onChange],
  );

  /** Handle selection from suggestions */
  const handlePartnerSelectInternal = useCallback(
    (partner: PartnerSuggestion) => {
      const newSnapshot: EntitySnapshot = {
        _id: partner._id,
        companyName: partner.companyName,
        registerationCode: partner.registerationCode,
        phoneNumber: partner.phoneNumber,
        emailAddress: partner.emailAddress,
        clientCode: partner.clientCode,
        address: {
          street: partner.address?.street || "",
          city: partner.address?.city || "",
          state: partner.address?.state || "",
          postalCode: partner.address?.postalCode || "",
          country:
            typeof partner.address?.country === "object"
              ? partner.address.country._id
              : partner.address?.country || "",
          // Carried through so a consumer that needs the map location (the
          // container order's route stops) gets it from the directory instead
          // of making the user re-pick an address that is already pinned.
          // Legacy records store `{ latitude: null, longitude: null }` — that
          // is "no location", so it is normalised to null here.
          coordinates:
            typeof partner.address?.coordinates?.latitude === "number" &&
            typeof partner.address?.coordinates?.longitude === "number"
              ? {
                  latitude: partner.address.coordinates.latitude,
                  longitude: partner.address.coordinates.longitude,
                }
              : null,
        },
        isTemporary: false,
      };

      setSearchTerm(partner.companyName);
      onChange(partner.companyName);
      onSnapshotSelect(newSnapshot);
      setIsOpen(false);
      dispatch(clearSuggestions());
    },
    [onChange, onSnapshotSelect, dispatch],
  );

  const handleClear = useCallback(() => {
    setSearchTerm("");
    onChange("");
    if (onClear) onClear();
  }, [onChange, onClear]);

  /** Click outside closes dropdown */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Sync external value */
  useEffect(() => setSearchTerm(value), [value]);

  /** Cleanup */
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dispatch(clearSuggestions());
      dispatch(clearAddressSuggestions());
    };
  }, [dispatch]);

  const sizeCls =
    size === "sm"
      ? "h-8 px-2.5 py-1 text-[13px] rounded-md"
      : "px-3 py-2 rounded-lg";
  const baseClasses = `w-full ${sizeCls} border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-400`;
  const errorClasses = error
    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
    : "";
  const disabledClasses = disabled
    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
    : "";

  const showClear = !disabled && searchTerm.length > 0;

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${baseClasses} ${errorClasses} ${disabledClasses} ${showClear ? "pr-8" : ""} ${className}`}
          autoComplete="off"
        />

        {localLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}

        {!localLoading && showClear && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-600"
            title={t("common:buttons.clear", { defaultValue: "Clear" })}
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/*
         * DEAD JSX — kept commented for reference. The pencil (Edit) and
         * "+ Add" buttons used to open the EntityModal. Inline fields below
         * the autocomplete replace this flow entirely.
         *
         * Edit pencil (visible only when a snapshot was pinned):
         * {hasSnapshot && !disabled && (
         *   <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2">
         *     <button
         *       type="button"
         *       onClick={onEditClick}
         *       className="text-blue-600 hover:text-blue-800"
         *       title="Edit"
         *     >
         *       <Edit2 className="h-4 w-4" />
         *     </button>
         *   </div>
         * )}
         *
         * "+ Add" button (visible only when no snapshot pinned), rendered
         * to the right of the input in a flex row:
         * {!hasSnapshot && (
         *   <button
         *     type="button"
         *     onClick={onAddClick}
         *     className="px-3 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
         *   >
         *     + {t("common:buttons.add")}
         *   </button>
         * )}
         */}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.length > 0 && (
            <>
              {suggestions.map((partner) => (
                <div
                  key={partner._id}
                  onClick={() => handlePartnerSelectInternal(partner)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="font-medium text-gray-900 truncate">
                          {partner.companyName}
                        </span>
                        {partner?.clientCode && (
                          <span className="font-medium text-gray-500 truncate">
                            ({partner.clientCode})
                          </span>
                        )}
                      </div>
                      {partner.address && (
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="truncate">
                            {partner.address?.street.split(",")[0]},{" "}
                            {partner.address?.city}, {partner.address?.state}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {suggestions.length === 0 && searchTerm.length >= 2 && (
            <div className="p-3 text-gray-500 text-sm text-center">
              {t("shipments:filters.noResults")} "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const PartnerAutocomplete = memo(PartnerAutocompleteComponent);
